<template>
  <li
    class="relative rounded-sm transition-colors"
    :class="showDocuments ? 'py-10' : 'group py-7'"
  >
    <div class="w-full">
      <!-- Breadcrumbs -->
      <p v-if="filterBreadcrumbs?.length && !showDocuments" class="mb-3 typo-body-md">
        <template v-for="(crumb, index) in filterBreadcrumbs" :key="crumb.id">
          <span> {{ crumb.title }} </span>
          <span
            v-if="index < filterBreadcrumbs.length - 1"
            class="px-2"
          >|</span>
        </template>
      </p>

      <!-- Title -->
      <h2 v-if="!showDocuments" class="mb-4 typo-heading-sm">
        {{ data.heading || data.title }}
      </h2>

      <NuxtLink
        v-else
        :to="data.url"
        class="
          group mb-4 flex items-center justify-between gap-10 transition-colors duration-300
          hover:text-blue-500
        "
      >
        <span class="typo-heading-sm">
          {{ data.heading || data.title }}
        </span>
        <!-- <IconArrowDown class="mr-3 size-6 -rotate-90 transition-transform group-hover:translate-x-3" /> -->
      </NuxtLink>

      <!-- Highlighted -->
      <ul v-if="!showDocuments" class="space-y-2">
        <li
          v-for="(highlightResult, index) in extractMatchLevelFull(
            data._highlightResult,
            []
          )"
          :key="index"
        >
          <p
            class="highlight typo-body-xl"
            v-html="extractSentenceWithHighlight(highlightResult.value)"
          />
        </li>
      </ul>
    </div>

    <!-- Link -->
    <NuxtLink
      v-if="!showDocuments"
      :to="data.url"
      class="
        mt-4 flex w-fit items-center justify-center gap-3 text-blue-500
        after:absolute after:inset-0
        group-hover:text-blue-600
      "
    >
      <span>{{ $t('learn_more') }}</span>
      <IconArrowDown class="size-6 -rotate-90 transition-transform group-hover:translate-x-3" />
    </NuxtLink>

    <!-- DOWNLOADS -->

    <ul v-show="showDocuments" class="flex flex-col gap-4">
      <li
        v-for="group in sortedFileGroups"
        :key="group.id"
        class="
          relative flex flex-wrap items-center gap-2 bg-grey-100 p-4 text-dark transition-colors duration-300
          group-hover:duration-500
          hover:bg-grey-200
        "
      >
        <div class="flex items-center gap-2">
          <IconFile class="aspect-square size-6 shrink-0 md:size-8 sm:size-7" />

          <h4 class="mr-2 text-body-lg font-medium md:text-body-2xl">
            {{ group.file_name }}
          </h4>
        </div>

        <div class="ml-auto flex items-center gap-3 sm:gap-4">
          <ul class="peer flex gp-y-2 flex-wrap">
            <li
              v-for="item in group.files"
              :key="item.id"
            >
              <a
                :href="item.file"
                target="_blank"
                :aria-label="`${group.file_name} ${item.language}`"
                download
                class="
                  px-2 text-body-lg font-medium underline decoration-transparent decoration-2
                  underline-offset-[6px] transition-[text-decoration-color] duration-300
                  hover:decoration-dark
                  md:text-body-2xl
                "
                :class="{ 'after:absolute after:inset-0': group.files.length === 1 }"
              >
                {{ item.language }}
              </a>
            </li>
          </ul>

          <IconDownload
            class="aspect-square size-6 shrink-0 md:size-8 peer-hover:hover-download-icon sm:size-7"
          />
        </div>
      </li>
    </ul>
  </li>
</template>

<script setup lang="ts">
const props = defineProps({
  searchQuery: {
    type: String,
    default: '',
  },
  data: {
    type: Object,
    default: () => ({}),
  },
  showDocuments: {
    type: Boolean,
    default: false,
  },
})

// COMPUTED
const filterBreadcrumbs = computed(() => {
  const currentUrl = props.data.url
  const { breadcrumbs } = props.data

  if (!breadcrumbs) {
    return []
  }

  return breadcrumbs.filter((crumb) => {
    if (breadcrumbs.length > 2) {
      return crumb.url !== '/' && crumb.url !== currentUrl
    }
    return crumb.url !== currentUrl
  })
})

const sortedFileGroups = computed(() => {
  let groups = props.data.downloads

  // If it's an object convert to array of values
  if (!Array.isArray(groups) && typeof groups === 'object' && groups !== null) {
    groups = Object.values(groups)
  } else if (!Array.isArray(groups)) {
    return []
  }

  // Now flatten the groups (array) into allFiles
  const allFiles = groups.flatMap(group => group.file_group || [])

  if (!props.searchQuery?.trim()) {
    // If no query, return sorted alphabetically
    return [...allFiles].sort((a, b) => a.file_name.localeCompare(b.file_name))
  }

  const queryLower = props.searchQuery.toLowerCase()

  // Map and sort
  const sorted = [...allFiles]
    .map((item) => {
      const nameLower = item.file_name?.toLowerCase() || ''
      let score = 0
      if (nameLower.startsWith(queryLower)) {
        score = 2
      } else if (nameLower.includes(queryLower)) {
        score = 1
      }
      return {
        ...item,
        score,
      }
    })
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score
      return a.file_name.localeCompare(b.file_name)
    })

  return sorted
})

// FUNCTIONS
function extractMatchLevelFull(object, result = [], counter = 0) {
  counter++

  for (const key in object) {
    // Exclude the title and heading from the highlighted results if they are not nested (on the first level) as they are already displayed as title
    if (counter === 1 && (key === 'title' || key === 'heading')) {
      continue
    }

    // Exclude the URL from the highlighted results
    if (key === 'url' || key === 'breadcrumbs') continue

    if (object[key] && typeof object[key] === 'object') {
      if (
        object[key].hasOwnProperty('matchLevel')
        && object[key].matchLevel !== 'none'
      ) {
        result.push(object[key])
      } else {
        extractMatchLevelFull(object[key], result)
      }
    }
  }
  return result
}

function extractSentenceWithHighlight(value) {
  const sentences = value.split(/(?<=[.?!])\s+/) // Split by sentence-ending punctuation followed by a space
  return sentences.find(sentence => sentence.includes('<em>'))
}
</script>

<style>
.highlight {
  em {
    @apply bg-grey-200 py-1 not-italic rounded-sm;
  }
}
</style>
